import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    XCircle,
    ThumbsDown,
    HelpCircle,
    ListChecks,
    MessageSquare,
    MoreHorizontal,
    Info,
    X,
    MessageCircle
} from 'lucide-react';
import { Theme } from '../types/theme';

interface ReviewFormProps {
    onSubmit: (feedback: { categories: string[]; comment: string }) => void;
    onCancel: () => void;
    theme: Theme;
}

const FEEDBACK_CATEGORIES = [
    { id: 'inaccurate', label: 'Inaccurate', icon: XCircle },
    { id: 'not_helpful', label: 'Not Helpful', icon: ThumbsDown },
    { id: 'confusing', label: 'Confusing', icon: HelpCircle },
    { id: 'incomplete', label: 'Incomplete', icon: ListChecks },
    { id: 'tone_style', label: 'Tone / Style', icon: MessageSquare },
    { id: 'other', label: 'Other', icon: MoreHorizontal }
];

const ReviewForm: React.FC<ReviewFormProps> = ({ onSubmit, onCancel, theme }) => {
    const [selected, setSelected] = useState<string | null>(null);
    const [comment, setComment] = useState('');
    const isOtherSelected = selected === 'Other' || selected === 'other' || (selected && FEEDBACK_CATEGORIES.find(c => c.label === selected)?.id === 'other');

    const handleSubmit = () => {
        if (!selected) return;
        onSubmit({ categories: [selected], comment });
    };

    const containerVariants = {
        hidden: {
            opacity: 0,
            y: 30,
            scale: 0.9,
            rotateX: 15,
            perspective: 1000
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            rotateX: 0,
            transition: {
                type: "spring" as const,
                stiffness: 260,
                damping: 22,
                mass: 1,
                staggerChildren: 0.08,
                delayChildren: 0.1
            }
        },
        exit: {
            opacity: 0,
            scale: 0.95,
            y: 10,
            transition: { duration: 0.2, ease: "easeOut" as const }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15, scale: 0.9, filter: 'blur(4px)' },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            filter: 'blur(0px)',
            transition: {
                type: "spring" as const,
                stiffness: 300,
                damping: 25
            }
        }
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="flex flex-col relative overflow-hidden rounded-2xl shadow-xl w-full mt-4"
            style={{
                backgroundColor: theme.surfaceColor,
                border: `1px solid ${theme.borderColor}`,
                backdropFilter: 'blur(10px)',
            }}
        >
            {/* Header - Balanced Padding */}
            <div className="relative px-5 pt-5 pb-3">
                <div className="flex justify-between items-start">
                    <div className="flex gap-3 items-center">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 500, delay: 0.1 }}
                            className="w-8 h-8 rounded-lg flex items-center justify-center shadow-sm"
                            style={{
                                backgroundColor: theme.primaryColor + '15',
                                color: theme.primaryColor,
                                border: `1px solid ${theme.primaryColor}30`
                            }}
                        >
                            <Info size={15} />
                        </motion.div>
                        <div>
                            <h3 className="text-sm font-bold tracking-tight leading-none" style={{ color: theme.textColor }}>
                                Help us improve
                            </h3>
                            <p className="text-[10px] font-medium opacity-60 mt-1" style={{ color: theme.textColor }}>
                                Your feedback shapes the future of this AI.
                            </p>
                        </div>
                    </div>
                    <motion.button
                        whileHover={{ rotate: 90, scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={onCancel}
                        className="p-1 rounded-full transition-colors hover:bg-black/5 dark:hover:bg-white/5"
                        style={{ color: theme.textColor }}
                    >
                        <X size={16} />
                    </motion.button>
                </div>
            </div>

            {/* Content Container - No internal scroll */}
            <div className="px-5 space-y-4 ">
                {/* Categories Grid - Balanced */}
                <div className="grid grid-cols-2 gap-2">
                    {FEEDBACK_CATEGORIES.map((cat) => {
                        const Icon = cat.icon;
                        const active = selected === cat.label;

                        return (
                            <motion.button
                                key={cat.id}
                                variants={itemVariants}
                                whileHover={{
                                    scale: 1.01,
                                    backgroundColor: active ? theme.primaryColor + '15' : theme.backgroundColor,
                                    borderColor: active ? theme.primaryColor : theme.borderColor,
                                }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setSelected(cat.label)}
                                className="group relative flex flex-col items-center gap-1.5 p-2 rounded-xl border transition-all duration-200"
                                style={{
                                    borderColor: active ? theme.primaryColor : theme.borderColor,
                                    backgroundColor: active ? theme.primaryColor + '10' : theme.backgroundColor,
                                    color: active ? theme.primaryColor : theme.textColor,
                                }}
                            >
                                <Icon
                                    size={17}
                                    className={`transition-all duration-300 group-hover:scale-105 ${active ? 'scale-110' : 'opacity-60'}`}
                                />
                                <span className={`text-[10px] font-bold tracking-wider uppercase transition-opacity ${active ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'}`}>
                                    {cat.label}
                                </span>
                                {active && (
                                    <motion.div
                                        layoutId="active-pill"
                                        className="absolute inset-0 rounded-xl pointer-events-none"
                                        style={{ border: `2px solid ${theme.primaryColor}` }}
                                    />
                                )}
                            </motion.button>
                        );
                    })}
                </div>

                {/* Comment Area - Balanced */}
                <motion.div
                    variants={itemVariants}
                    className="space-y-2"
                >
                    <div className="flex items-center gap-1.5 px-0.5">
                        <MessageCircle size={12} className="opacity-50" style={{ color: theme.textColor }} />
                        <label className="text-[10px] font-bold uppercase tracking-wider opacity-60" style={{ color: theme.textColor }}>
                            Optional details
                        </label>
                    </div>
                    <div className="relative group">
                        <textarea
                            rows={2}
                            disabled={!selected && !isOtherSelected}
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                            placeholder={
                                isOtherSelected
                                    ? 'Describe the issue in detail...'
                                    : 'Tell us more about your experience...'
                            }
                            className="w-full text-xs p-3 rounded-xl border-2 resize-none outline-none transition-all duration-300 focus:ring-0"
                            style={{
                                backgroundColor: theme.backgroundColor,
                                borderColor: selected ? theme.primaryColor + '30' : theme.borderColor,
                                color: theme.textColor,
                                opacity: selected ? 1 : 0.6,
                                cursor: selected ? 'text' : 'not-allowed',
                                boxShadow: selected ? '0 0 0 3px ' + theme.primaryColor + '08' : 'none'
                            }}
                        />
                    </div>
                </motion.div>
            </div>

            {/* Footer Buttons - Equal Width */}
            <div className="px-5 py-4 flex gap-3 items-center" style={{ borderTop: `1px solid ${theme.borderColor}` }}>
                <motion.button
                    whileHover={{ scale: 1.02, backgroundColor: theme.backgroundColor }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onCancel}
                    className="flex-1 px-4 py-2 text-xs font-bold rounded-xl transition-colors border"
                    style={{
                        borderColor: theme.borderColor,
                        backgroundColor: theme.backgroundColor,
                        color: theme.textColor,
                        opacity: 0.8
                    }}
                >
                    Cancel
                </motion.button>

                <motion.button
                    whileHover={selected ? {
                        scale: 1.02,
                        boxShadow: "0 6px 15px -4px " + theme.primaryColor + "50"
                    } : {}}
                    whileTap={selected ? { scale: 0.98 } : {}}
                    disabled={!selected}
                    onClick={handleSubmit}
                    className="flex-1 px-4 py-2 text-xs font-bold rounded-xl text-white transition-all shadow-md flex items-center justify-center gap-2"
                    style={{
                        backgroundColor: theme.primaryColor,
                        opacity: selected ? 1 : 0.5,
                        cursor: selected ? 'pointer' : 'not-allowed'
                    }}
                >
                    Send Feedback
                </motion.button>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: ${theme.borderColor};
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: ${theme.primaryColor}50;
                }
            `}} />
        </motion.div>
    );
};

export default ReviewForm;
